import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, CircularProgress, Alert, Chip, MenuItem, TablePagination,
} from '@mui/material';
import { Add, Cancel } from '@mui/icons-material';
import { amenityApi, unitApi } from '../../services/api';
import { AmenityBooking, Amenity, Unit } from '../../types';

const AmenityBookingList: React.FC = () => {
  const [bookings, setBookings] = useState<AmenityBooking[]>([]);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [amenityId, setAmenityId] = useState('');
  const [bookUnitId, setBookUnitId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [bRes, aRes, uRes] = await Promise.all([
        amenityApi.getMyBookings(),
        amenityApi.getAll(),
        unitApi.getAll(),
      ]);
      setBookings(Array.isArray(bRes.data) ? bRes.data : bRes.data?.data || []);
      setAmenities(Array.isArray(aRes.data) ? aRes.data : aRes.data?.data || []);
      setUnits(Array.isArray(uRes.data) ? uRes.data : uRes.data?.data || []);
    } catch (err: any) {
      setError('Failed to load bookings.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAddDialog = () => {
    setAmenityId(amenities[0]?.id ? String(amenities[0].id) : '');
    setBookUnitId(units[0]?.id ? String(units[0].id) : '');
    const now = new Date();
    const later = new Date(now.getTime() + 3600000);
    setStartTime(now.toISOString().slice(0, 16));
    setEndTime(later.toISOString().slice(0, 16));
    setNotes('');
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!amenityId) { setFormError('Amenity is required.'); return; }
    if (!bookUnitId) { setFormError('Unit is required.'); return; }
    if (new Date(startTime) >= new Date(endTime)) { setFormError('Start must be before end.'); return; }
    setSaving(true); setFormError('');
    try {
      await amenityApi.createBooking({
        amenityId: Number(amenityId),
        unitId: Number(bookUnitId),
        startTime: new Date(startTime).toISOString(),
        endTime: new Date(endTime).toISOString(),
        notes: notes.trim(),
      });
      await fetchData();
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to create booking.');
    } finally { setSaving(false); }
  };

  const handleCancel = async (id: number) => {
    try {
      await amenityApi.cancelBooking(id);
      await fetchData();
    } catch (err: any) {
      setError('Failed to cancel booking.');
    }
  };

  const formatDateTime = (s: string) => new Date(s).toLocaleString();
  const displayed = bookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const activeAmenities = amenities.filter(a => a.isActive);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Amenity Bookings</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog} disabled={activeAmenities.length === 0 || units.length === 0}>
          New Booking
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Amenity</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>End</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Fee</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No bookings.</Typography></TableCell></TableRow>
              ) : displayed.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>{b.amenityName || `#${b.amenityId}`}</TableCell>
                  <TableCell>{b.unitNumber || `#${b.unitId}`}</TableCell>
                  <TableCell>{formatDateTime(b.startTime)}</TableCell>
                  <TableCell>{formatDateTime(b.endTime)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: b.feeAmount > 0 ? 'primary.main' : 'text.secondary' }}>
                      {b.feeAmount > 0 ? `RM${b.feeAmount.toFixed(2)}` : 'Free'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={b.status} color={b.status === 'Confirmed' ? 'success' : b.status === 'Cancelled' ? 'error' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    {b.status === 'Confirmed' && (
                      <IconButton size="small" color="error" onClick={() => handleCancel(b.id)} title="Cancel booking"><Cancel /></IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={bookings.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Booking</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <TextField label="Amenity" select fullWidth margin="normal" value={amenityId} onChange={e => setAmenityId(e.target.value)} required disabled={saving}>
            {activeAmenities.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.name} (capacity: {a.capacity})</MenuItem>)}
          </TextField>
          <TextField label="Unit" select fullWidth margin="normal" value={bookUnitId} onChange={e => setBookUnitId(e.target.value)} required disabled={saving}>
            {units.map(u => <MenuItem key={u.id} value={String(u.id)}>{u.unitNumber}</MenuItem>)}
          </TextField>
          <TextField label="Start Time" type="datetime-local" fullWidth margin="normal" value={startTime} onChange={e => setStartTime(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} required disabled={saving} />
          <TextField label="End Time" type="datetime-local" fullWidth margin="normal" value={endTime} onChange={e => setEndTime(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} required disabled={saving} />
          <TextField label="Notes" fullWidth margin="normal" value={notes} onChange={e => setNotes(e.target.value)} multiline rows={2} disabled={saving} />
          {amenityId && startTime && endTime && (() => {
            const selAmenity = amenities.find(a => String(a.id) === amenityId);
            if (!selAmenity || !selAmenity.baseFee) return null;
            const hours = (new Date(endTime).getTime() - new Date(startTime).getTime()) / 3600000;
            if (hours <= 0) return null;
            let fee: number;
            if (!selAmenity.baseDurationHours || selAmenity.baseDurationHours === 0) {
              fee = selAmenity.baseFee;
            } else if (hours <= selAmenity.baseDurationHours) {
              fee = selAmenity.baseFee;
            } else {
              fee = selAmenity.baseFee + Math.ceil(hours - selAmenity.baseDurationHours) * selAmenity.hourlyRate;
            }
            return (
              <Alert severity="info" sx={{ mt: 2 }}>
                Estimated fee: <strong>RM{fee.toFixed(2)}</strong> ({hours.toFixed(1)} hours)
              </Alert>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} /> : undefined}>{saving ? 'Saving...' : 'Book'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AmenityBookingList;
